import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ContactField } from '@models/contact/field';
import { NgSelectComponent } from '@ng-select/ng-select';
import { ContactService } from '@services/contact.service';
import { EmailCampaignListService } from '@services/email-campaign-list.service';
import { EmailCreateTabManagerService } from '@services/email-create-tab-manager.service';
import { EmailHistoryService } from '@services/email-history.service';
import { EmailSendingService } from '@services/email-sending.service';
import { LoaderService } from '@services/loader.service';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'wsb-email-create-setup',
  templateUrl: './email-create-setup.component.html',
  styleUrls: ['./email-create-setup.component.scss'],
  standalone: false
})
export class EmailCreateSetupComponent implements OnInit, OnDestroy {
  @Input() emailCampaignIssue;
  @Output() enableNextButton: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() template: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('dynamicsFieldsSelection') dynamicsFieldsSelection: NgSelectComponent;

  setupForm: UntypedFormGroup;
  list: UntypedFormControl = new UntypedFormControl();
  dynamicsFields = [];
  emailCampaignList = [];
  copy = false;
  edit = false;
  listener: Subscription;
  constructor(
    private fb: UntypedFormBuilder,
    private emailSendingService: EmailSendingService,
    private emailHistoryService: EmailHistoryService,
    private loaderService: LoaderService,
    private contactsService: ContactService,
    private emailCampaignListService: EmailCampaignListService,
    private route: ActivatedRoute,
    public emailCreateTabManagerService: EmailCreateTabManagerService
  ) {}

  ngOnInit(): void {
    this.generateForm();

    this.getDefaultTitle();

    this.getContactsDynamicField();

    this.getEmailCampaignList();

    this.setForm();

    this.checkRoute();

    this.listenToNextButton();
  }

  checkRoute() {
    this.route.queryParams.subscribe(params => {
      if (params.edit) {
        this.emailHistoryService.getEmailCampaignIssue(params.edit).subscribe(emailCampaignIssue => {
          this.emailCampaignIssue = emailCampaignIssue;
          this.setupForm.patchValue(emailCampaignIssue);
          this.template.emit(emailCampaignIssue.messageHtml);
          this.edit = true;
        });
      }
      if (params.copy) {
        this.emailHistoryService.getEmailCampaignIssue(params.copy).subscribe(emailCampaignIssue => {
          this.setupForm.patchValue(emailCampaignIssue);
          this.template.emit(emailCampaignIssue.messageHtml);
          this.copy = true;
        });
      }
    });
  }

  listenToNextButton() {
    this.listener = this.emailCreateTabManagerService.lgNext$.subscribe(tab => {
      if (tab === 1) {
        this.addIssue();
      }
    });
  }

  selectionChange(event: ContactField) {
    if (event) {
      if (this.setupForm.get('subject').value) {
        this.setupForm.get('subject').setValue(this.setupForm.get('subject').value + ' ' + event.placeholder);
      } else {
        this.setupForm.get('subject').setValue(event.placeholder);
      }
    }
    this.dynamicsFieldsSelection.handleClearClick();
  }

  generateForm() {
    this.setupForm = this.fb.group({
      name: [],
      subject: ['', Validators.required],
      dynamicField: [],
      webAnalitics: []
    });

    this.formListener();
  }

  setForm() {
    if (this.emailCampaignIssue) {
      this.setupForm.patchValue(this.emailCampaignIssue);
    }
  }

  formListener() {
    this.setupForm.valueChanges.subscribe(() => {
      if (this.setupForm.valid) {
        return this.enableNextButton.emit(true);
      }
      this.enableNextButton.emit(false);
    });
  }

  getDefaultTitle() {
    this.loaderService.show();
    this.emailSendingService
      .getDefaultTitle()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(title => {
        this.setupForm.patchValue({ name: title?.defaultTitle });
      });
  }

  getContactsDynamicField() {
    this.loaderService.show();
    this.contactsService
      .getFields()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(fields => {
        this.dynamicsFields = fields.data.filter(field => field.idField !== 'PHONE');
      });
  }

  getEmailCampaignList() {
    this.loaderService.show();
    this.emailCampaignListService
      .getListCampaignEmail()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(list => {
        this.emailCampaignList = list.filter(x => x.status === 'ACTIVE');
        if (this.emailCampaignList.length !== 0) {
          this.list.patchValue(this.emailCampaignList[0].idCampaign);
        }
      });
  }

  addIssue() {
    let obs$ = null;
    if (this.emailCampaignIssue) {
      obs$ = this.emailSendingService.updateConfiguration(this.emailCampaignIssue.idCampaignIssue, this.setupForm.value);
    } else {
      obs$ = this.emailSendingService.addNewEmailIssueCampaign(this.list.value, this.setupForm.value);
    }
    this.emailCreateTabManagerService.setTabIsReady(obs$);

    if (this.edit) {
      return this.emailCreateTabManagerService.lgDone('editor');
    } else if (this.copy) {
      return this.emailCreateTabManagerService.lgDone('copy');
    } else {
      return this.emailCreateTabManagerService.lgDone();
    }
  }

  ngOnDestroy(): void {
    this.listener.unsubscribe();
  }
}
